import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { clearSession, logout } from "../../services/auth";

const IMPERSONATION_KEY = "@impersonacao";
const LOGGED_KEY = "@logged";
const TOKEN_KEY = "@agroReceita-Token";

const Banner = styled.div`
  width: 100%;
  min-height: 44px;
  background: ${(props) => (props.isCritical ? "#ffe3e3" : "#fff4d6")};
  border-bottom: 1px solid ${(props) => (props.isCritical ? "#ff6b6b" : "#f2cf75")};
  color: ${(props) => (props.isCritical ? "#7a1010" : "#5a4100")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.2px;
  flex-wrap: wrap;
  animation: ${(props) => (props.isCritical ? "impersonationAlertPulse 1s ease-in-out infinite" : "none")};

  @keyframes impersonationAlertPulse {
    0% {
      background: #ffe3e3;
      border-bottom-color: #ff6b6b;
    }
    50% {
      background: #ffd1d1;
      border-bottom-color: #ff3b3b;
    }
    100% {
      background: #ffe3e3;
      border-bottom-color: #ff6b6b;
    }
  }
`;

const Timer = styled.span`
  background: ${(props) => (props.isCritical ? "#a30000" : "#5a4100")};
  color: #fff;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  min-width: 74px;
  text-align: center;
  animation: ${(props) => (props.isCritical ? "impersonationTimerBlink 0.9s linear infinite" : "none")};

  @keyframes impersonationTimerBlink {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.55;
    }
    100% {
      opacity: 1;
    }
  }
`;

const readImpersonation = () => {
  try {
    const value = localStorage.getItem(IMPERSONATION_KEY);
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const hasExplicitTimezone = (value) => /[zZ]|[+\-]\d{2}:?\d{2}$/.test(value);

const parseExpirationMs = (value, timezone) => {
  if (value === null || value === undefined) return NaN;

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return NaN;
    return value > 1e12 ? value : value * 1000;
  }

  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return NaN;

    if (/^\d+$/.test(raw)) {
      const numeric = Number(raw);
      if (!Number.isFinite(numeric)) return NaN;
      return numeric > 1e12 ? numeric : numeric * 1000;
    }

    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) return parsed;

    let normalized = raw.replace(" ", "T");
    if (!hasExplicitTimezone(normalized) && timezone === "America/Sao_Paulo") {
      normalized = `${normalized}-03:00`;
    }
    const parsedNormalized = Date.parse(normalized);
    if (!Number.isNaN(parsedNormalized)) return parsedNormalized;
  }

  return NaN;
};

const toClock = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

const isSameImpersonation = (a, b) =>
  a?.ativo === b?.ativo &&
  a?.expira_em === b?.expira_em &&
  a?.ticket_id === b?.ticket_id &&
  a?.operador_adm_id === b?.operador_adm_id;

const ImpersonationBanner = () => {
  const location = useLocation();
  const isExpiringOutRef = useRef(false);
  const [impersonation, setImpersonation] = useState(readImpersonation);
  const [remainingMs, setRemainingMs] = useState(0);
  const isLogged = localStorage.getItem(LOGGED_KEY) === "true";
  const hasToken = Boolean(localStorage.getItem(TOKEN_KEY));
  const isLoginRoute = location.pathname === "/login" || location.pathname.startsWith("/login");
  const isCritical = remainingMs > 0 && remainingMs <= 10 * 60 * 1000;

  useEffect(() => {
    const onStorage = (event) => {
      if (!event.key || event.key === IMPERSONATION_KEY) {
        setImpersonation(readImpersonation());
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const expirationMs = useMemo(() => {
    if (!impersonation?.expira_em) {
      return NaN;
    }
    return parseExpirationMs(impersonation.expira_em, impersonation.timezone);
  }, [impersonation]);

  useEffect(() => {
    if (!isLogged || !hasToken || isLoginRoute) {
      if (impersonation !== null) {
        setImpersonation(null);
      }
      setRemainingMs(0);
      return undefined;
    }

    if (!impersonation?.ativo || Number.isNaN(expirationMs)) {
      setRemainingMs(0);
      return undefined;
    }

    const updateRemaining = () => {
      const latest = readImpersonation();
      setImpersonation((current) => (isSameImpersonation(current, latest) ? current : latest));

      if (!latest?.ativo) {
        setRemainingMs(0);
        return;
      }

      const latestExpirationMs = parseExpirationMs(latest.expira_em, latest.timezone);
      const nextRemainingMs = Number.isNaN(latestExpirationMs)
        ? 0
        : Math.max(latestExpirationMs - Date.now(), 0);

      if (nextRemainingMs <= 0) {
        setImpersonation(null);
        setRemainingMs(0);
        if (!isExpiringOutRef.current) {
          isExpiringOutRef.current = true;
          Promise.resolve(logout())
            .catch(() => clearSession())
            .finally(() => {
              window.location.href = "/login";
            });
        }
        return;
      }

      setRemainingMs(nextRemainingMs);
    };

    updateRemaining();
    const timer = setInterval(updateRemaining, 1000);
    return () => clearInterval(timer);
  }, [impersonation, expirationMs, isLogged, hasToken, isLoginRoute]);

  if (!isLogged || !hasToken || isLoginRoute || !impersonation?.ativo) {
    return null;
  }

  return (
    <Banner isCritical={isCritical}>
      <span>Acesso por Impersonificação</span>
      <span>
        Tempo restante: <Timer isCritical={isCritical}>{toClock(remainingMs)}</Timer>
      </span>
    </Banner>
  );
};

export default ImpersonationBanner;
